import React, { useMemo, useRef } from 'react';
import { Trail } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const smoothstep = (edge0: number, edge1: number, x: number) => {
  const t = THREE.MathUtils.clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
};

const createGlowTexture = (size = 128) => {
  const data = new Uint8Array(size * size * 4);
  const half = size / 2;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x - half) / half;
      const dy = (y - half) / half;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const falloff = Math.pow(Math.max(0, 1 - distance), 3.2);
      const alpha = Math.floor(255 * falloff);
      const idx = (x + y * size) * 4;
      data[idx] = 255;
      data[idx + 1] = 255;
      data[idx + 2] = 255;
      data[idx + 3] = alpha;
    }
  }

  const texture = new THREE.DataTexture(data, size, size);
  texture.needsUpdate = true;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  return texture;
};

interface ShootingStarConfig {
  curve: THREE.CatmullRomCurve3;
  trailColor: THREE.ColorRepresentation;
  trailWidth: number;
  trailLength: number;
  trailDecay: number;
  attenuation: (t: number) => number;
  interval: number;
  stride: number;
  headRadius: number;
  glowScale: number;
  coreColorRGB: Readonly<[number, number, number]>;
  spriteColor: THREE.ColorRepresentation;
  duration: number;
  rest: number;
  offset: number;
  wobbleAmplitude: number;
  wobbleFrequency: number;
}

interface ShootingStarProps extends ShootingStarConfig {
  glowMap: THREE.Texture;
}

const ShootingStar: React.FC<ShootingStarProps> = ({
  curve,
  trailColor,
  trailWidth,
  trailLength,
  trailDecay,
  attenuation,
  interval,
  stride,
  headRadius,
  glowScale,
  coreColorRGB,
  spriteColor,
  duration,
  rest,
  offset,
  wobbleAmplitude,
  wobbleFrequency,
  glowMap,
}) => {
  const groupRef = useRef<THREE.Group>(null);
  const spriteMaterialRef = useRef<THREE.SpriteMaterial>(null);
  const coreMaterialRef = useRef<THREE.MeshBasicMaterial>(null);
  const trailRef = useRef<any>(null);
  const trailInitialized = useRef(false);
  const lastPoint = useMemo(() => new THREE.Vector3(), []);
  const tempPoint = useMemo(() => new THREE.Vector3(), []);
  const tangent = useMemo(() => new THREE.Vector3(), []);
  const wobbleAxis = useMemo(() => new THREE.Vector3(), []);
  const baseSpriteColor = useMemo(() => new THREE.Color(spriteColor), [spriteColor]);
  const spriteWorking = useMemo(() => new THREE.Color(), []);
  const baseCoreColor = useMemo(() => {
    const color = new THREE.Color();
    color.setRGB(coreColorRGB[0], coreColorRGB[1], coreColorRGB[2]);
    return color;
  }, [coreColorRGB]);
  const coreWorking = useMemo(() => new THREE.Color(), []);
  const up = useMemo(() => new THREE.Vector3(0, 1, 0), []);
  const right = useMemo(() => new THREE.Vector3(1, 0, 0), []);
  const totalDuration = duration + rest;
  const previousCycleTime = useRef(0);

  useFrame(({ clock }) => {
    if (!groupRef.current) {
      return;
    }

    const elapsed = clock.getElapsedTime();
    const cycleTime = (elapsed + offset) % totalDuration;
    const wasReset = cycleTime < previousCycleTime.current;
    previousCycleTime.current = cycleTime;
    const active = cycleTime < duration;
    const travel = active ? cycleTime / duration : 1;

    if (wasReset) {
      curve.getPointAt(0, lastPoint);
      groupRef.current.position.copy(lastPoint);
    }

    if (active) {
      curve.getPointAt(travel, tempPoint);
      curve.getTangentAt(travel, tangent);

      wobbleAxis.crossVectors(tangent, up);
      if (wobbleAxis.lengthSq() < 1e-6) {
        wobbleAxis.crossVectors(tangent, right);
      }
      wobbleAxis.normalize().multiplyScalar(
        Math.sin((elapsed + offset) * wobbleFrequency + travel * Math.PI * 2) * wobbleAmplitude
      );
      tempPoint.add(wobbleAxis);

      groupRef.current.position.copy(tempPoint);
      lastPoint.copy(tempPoint);
    } else {
      groupRef.current.position.copy(lastPoint);
    }

    const fadeIn = smoothstep(0, 0.18, travel);
    const fadeOut = smoothstep(0.75, 1, travel);
    const activeOpacity = fadeIn * (1 - fadeOut);
    const tailFade = Math.max(0, 1 - (cycleTime - duration) / rest);
    const headOpacity = active ? activeOpacity : tailFade * 0.35;
    const trailOpacity = active ? Math.max(0.25, activeOpacity) : tailFade * 0.2;

    if (spriteMaterialRef.current) {
      spriteMaterialRef.current.opacity = headOpacity;
      spriteMaterialRef.current.depthWrite = false;
      spriteMaterialRef.current.toneMapped = false;
      spriteMaterialRef.current.blending = THREE.AdditiveBlending;
      spriteWorking.copy(baseSpriteColor).multiplyScalar(1 + headOpacity * 0.4);
      spriteMaterialRef.current.color.copy(spriteWorking);
    }

    if (coreMaterialRef.current) {
      const emissionBoost = 0.7 + headOpacity * 0.45;
      coreMaterialRef.current.opacity = headOpacity;
      coreMaterialRef.current.toneMapped = false;
      coreMaterialRef.current.depthWrite = false;
      coreMaterialRef.current.transparent = true;
      coreWorking.copy(baseCoreColor).multiplyScalar(emissionBoost);
      coreMaterialRef.current.color.copy(coreWorking);
    }

    if (trailRef.current?.material) {
      const material = trailRef.current.material as THREE.Material & {
        opacity?: number;
        transparent?: boolean;
        depthWrite?: boolean;
        blending?: THREE.Blending;
      };
      if (!trailInitialized.current) {
        material.transparent = true;
        material.depthWrite = false;
        material.blending = THREE.AdditiveBlending;
        material.opacity = 0;
        trailInitialized.current = true;
      }
      if (typeof material.opacity === 'number') {
        material.opacity = THREE.MathUtils.lerp(material.opacity, trailOpacity, 0.18);
      }
    }
  });

  return (
    <Trail
      ref={trailRef}
      width={trailWidth}
      length={trailLength}
      decay={trailDecay}
      color={trailColor}
      attenuation={attenuation}
      interval={interval}
      stride={stride}
    >
      <group ref={groupRef}>
        <sprite scale={[headRadius * glowScale, headRadius * glowScale, 1]}>
          <spriteMaterial
            ref={spriteMaterialRef}
            map={glowMap}
            transparent
            opacity={0}
            depthWrite={false}
            toneMapped={false}
            blending={THREE.AdditiveBlending}
          />
        </sprite>
        <mesh scale={[headRadius, headRadius, headRadius]}>
          <sphereGeometry args={[0.6, 24, 24]} />
          <meshBasicMaterial
            ref={coreMaterialRef}
            color={baseCoreColor}
            toneMapped={false}
            transparent
            opacity={0}
            depthWrite={false}
          />
        </mesh>
      </group>
    </Trail>
  );
};

const ShootingStars: React.FC = () => {
  const glowMap = useMemo(() => createGlowTexture(128), []);
  const starConfigs = useMemo<ShootingStarConfig[]>(() => {
    const makeCurve = (points: Array<[number, number, number]>, tension = 0.6) =>
      new THREE.CatmullRomCurve3(
        points.map(([x, y, z]) => new THREE.Vector3(x, y, z)),
        false,
        'catmullrom',
        tension
      );

    return [
      {
        curve: makeCurve(
          [
            [320, 85, -380],
            [192, 48, -240],
            [80, 32, -128],
            [-40, 10, -26],
            [-160, -16, 52],
          ],
          0.68
        ),
        trailColor: '#e6f7ff',
        trailWidth: 0.32,
        trailLength: 2.4,
        trailDecay: 1.8,
        attenuation: (t: number) => Math.pow(1 - t, 1.8),
        interval: 0.015,
        stride: 0,
  headRadius: 0.42,
  glowScale: 4.8,
  coreColorRGB: [2.8, 2.8, 2.4],
        spriteColor: '#fdf7ff',
        duration: 2.6,
        rest: 1.4,
        offset: 0,
        wobbleAmplitude: 1.5,
        wobbleFrequency: 4.5,
      },
      {
        curve: makeCurve(
          [
            [-370, -74, 320],
            [-256, -37, 197],
            [-112, -26, 80],
            [48, -16, -26],
            [171, 0, -123],
            [293, 16, -187],
          ],
          0.62
        ),
        trailColor: '#bbf6ff',
        trailWidth: 0.28,
        trailLength: 2.1,
        trailDecay: 1.9,
        attenuation: (t: number) => Math.pow(1 - t, 2.0),
        interval: 0.018,
        stride: 0,
  headRadius: 0.38,
  glowScale: 5.2,
  coreColorRGB: [2.3, 2.5, 2.7],
        spriteColor: '#e1fbff',
        duration: 3,
        rest: 1.9,
        offset: 0.8,
        wobbleAmplitude: 1.2,
        wobbleFrequency: 5.2,
      },
      {
        curve: makeCurve(
          [
            [-240, 128, -320],
            [-107, 69, -192],
            [-27, 48, -59],
            [69, 16, 48],
            [181, -5, 128],
            [320, -27, 208],
          ],
          0.58
        ),
        trailColor: '#ffd9ff',
        trailWidth: 0.3,
        trailLength: 2.6,
        trailDecay: 1.85,
        attenuation: (t: number) => Math.pow(1 - t, 1.9),
        interval: 0.016,
        stride: 0,
  headRadius: 0.4,
  glowScale: 5.0,
  coreColorRGB: [2.6, 1.9, 2.8],
        spriteColor: '#ffe9ff',
        duration: 2.9,
        rest: 2.1,
        offset: 1.9,
        wobbleAmplitude: 1.35,
        wobbleFrequency: 4.2,
      },
    ];
  }, []);

  return (
    <group position={[0, 0, -420]}>
      {starConfigs.map((config, index) => (
        <ShootingStar key={index} glowMap={glowMap} {...config} />
      ))}
    </group>
  );
};

export default ShootingStars;